/**
 * Blender modifier types supported by PoseRef.
 *
 * Each modifier is a tagged union (discriminated by `kind`).
 * Adding a new modifier type means:
 * 1. Add an interface here
 * 2. Add it to the BlendModifier union
 * 3. Add extraction logic in ModifierExtractor.ts
 * 4. Add application logic in modifierApply.ts
 */

export interface BlendModifierBase<TKind extends string> {
  kind: TKind;
  name: string;
  enabled: boolean;
}

export interface BlendSubsurfModifier extends BlendModifierBase<"subsurf"> {
  /** 0 = Catmull-Clark, 1 = Simple */
  subdivType: number;
  /** Viewport subdivision level */
  levels: number;
  /** Render subdivision level */
  renderLevels: number;
}

export interface BlendMirrorModifier extends BlendModifierBase<"mirror"> {
  /** Mirror on X axis */
  axisX: boolean;
  /** Mirror on Y axis */
  axisY: boolean;
  /** Mirror on Z axis */
  axisZ: boolean;
  /** Whether to merge vertices at the mirror seam */
  merge: boolean;
  /** Prevent vertices from crossing the mirror plane during editing */
  clipping: boolean;
  /** Merge tolerance for vertices at seam */
  tolerance: number;
  /** Name of the mirror object (null = mirror around own origin) */
  mirrorObjectName: string | null;
  /** Mirror center in object local space (computed at render time) */
  mirrorOrigin: [number, number, number] | null;
}

export interface BlendSolidifyModifier extends BlendModifierBase<"solidify"> {
  /** Shell thickness */
  offset: number;
  /** -1 = outward, 0 = centered, 1 = inward */
  offsetDirection: number;
  /** Optional vertex group (by stable id) on the owner mesh that restricts
   *  the inner shell to faces whose every vertex belongs to the group.
   *  Null = solidify the entire mesh. */
  vertexGroupId: string | null;
  /** Runtime-resolved: source-space vertex indices belonging to `vertexGroupId`,
   *  populated by `resolveModifierEvaluation`. Never authored or persisted. */
  vertexGroupIndices?: Uint32Array | null;
}

export interface BlendArrayModifier extends BlendModifierBase<"array"> {
  /** Number of copies, including the original */
  count: number;
  /** Offset each copy by a fraction of the object bounding box */
  useRelativeOffset: boolean;
  /** Per-axis bounding-box fraction added per copy */
  relativeOffset: [number, number, number];
  /** Offset each copy by a fixed amount in object space */
  useConstantOffset: boolean;
  /** Fixed offset (object-space units) added per copy */
  constantOffset: [number, number, number];
  /** Name of the object used as offset (null = none) */
  offsetObjectName: string | null;
  /**
   * Column-major 4×4 TRS world matrix of the offset object.
   * Copy i gets M^i applied (cumulative matrix multiplication).
   * Null when no offset object is assigned.
   */
  offsetMatrix: number[] | null;
}

/** Visual style of the generated skin tube geometry. */
export type SkinStyle = "smooth" | "cable" | "ribbed" | "bundle" | "chain";

export type SkinCapStyle = "flat" | "ferrule" | "none";

export interface BlendSkinModifier extends BlendModifierBase<"skin"> {
  /** Global tube radius (meters). Per-vertex radius multipliers may be added in future. */
  depth: number;
  /** Number of vertices around the cross-section circle (>= 3). */
  subdivisions: number;
  /** Whether to emit fan-triangulated caps at 1-valent (endpoint) vertices. */
  fillCaps: boolean;
  /**
   * Style of end caps at 1-valent (endpoint) vertices:
   * - "flat":    fan-triangulated disc flush with the tube end (default)
   * - "ferrule": short cylindrical band, slightly wider than the cable, capped flat —
   *              like a metal sleeve holding rope strands together
   * - "none":    no cap geometry
   * When set, takes precedence over `fillCaps`.
   */
  capStyle: SkinCapStyle;
  /**
   * Scale ring radii at bent joints by 1/cos(halfBendAngle) to compensate for
   * the apparent thinning that occurs when a tube bends sharply. Capped at 3×
   * to prevent extreme inflation at very tight corners.
   */
  preserveVolume: boolean;
  /**
   * Visual style of the generated tube:
   * - "smooth": solid circular tube (default, original behaviour)
   * - "cable":  N helical strands wound around the main path axis
   * - "ribbed": single tube with sinusoidally modulated radius (ridged sheathing)
   * - "bundle": N parallel wires area-packed with random diameter jitter (cable harness)
   * - "chain":  sequence of interlocking torus / stadium-shaped links along the path
   */
  style: SkinStyle;
  // --- Cable / Bundle shared params ---
  /** Number of strands or wires (2–24). Used when style === "cable" or "bundle". */
  strandCount: number;
  /** Helix turns per meter along the path. Only used when style === "cable". */
  twistRate: number;
  // --- Bundle style params ---
  /** Per-wire radius variation fraction (0 = uniform, 1 = ±100%). Only used when style === "bundle". */
  bundleJitter: number;
  // --- Ribbed style params ---
  /** Rib ridges per metre along the path. Only used when style === "ribbed". */
  ribFrequency: number;
  /** Fraction of radius removed at rib troughs (0 = flat, 1 = full depth). Only used when style === "ribbed". */
  ribDepth: number;
  // --- Chain style params (all in absolute meters / degrees, independent of `depth`) ---
  /** Outer length of each link along the path tangent (m). Only used when style === "chain". */
  linkLength: number;
  /** Width-to-length ratio of each link (1 = circular torus, <1 = elongated stadium). Only used when style === "chain". */
  linkAspect: number;
  /** Wire (torus minor) radius (m). Only used when style === "chain". */
  wireThickness: number;
  /** Fraction of link length to overlap with the next link (0 = touching, ~0.4 = interlocked). Only used when style === "chain". */
  linkOverlap: number;
  /** Rotation between adjacent links around the path tangent, in degrees (90 = classic chain). Only used when style === "chain". */
  twistPerLink: number;
  /** Centerline ring resolution per link (>= 8). Only used when style === "chain". */
  linkSegments: number;
  /** Optional vertex group (by stable id) on the owner mesh that restricts
   *  skin tube generation to wire edges whose both endpoints belong to the group.
   *  Null = skin all wire edges. */
  vertexGroupId: string | null;
  /** Runtime-resolved: source-space vertex indices belonging to `vertexGroupId`,
   *  populated by `resolveModifierEvaluation`. Never authored or persisted. */
  vertexGroupIndices?: Uint32Array | null;
}

export interface BlendInstanceModifier extends BlendModifierBase<"instance"> {
  /** Name of the scene object whose local geometry is instanced. */
  sourceObjectName: string | null;
  /** Where to place instances: at the owner's origin, or at each vertex/edge/face. */
  placementMode: "origin" | "vertices" | "edges" | "faces";
  /** Orientation of each instance relative to the placement surface. */
  orientation: "local" | "normal" | "global";
  /** Maximum number of elements (edges/faces/vertices) to place instances on. */
  instanceLimit: 64 | 128 | 256 | 512;
  /** When true (edges/faces only), place multiple randomly-positioned instances per element. */
  scatterMode: boolean;
  /** Number of instances placed per edge/face when scatterMode is enabled. */
  instancesPerElement: number;
  /** When true, apply random scale and rotation variation to each instance. */
  jitterEnabled: boolean;
  /** Scale variation (0 = none, 1 = ±100%). */
  jitterScale: number;
  /** Rotation variation around the placement normal (0 = none, 1 = full 360°). */
  jitterRotation: number;
  /** When true, render instances using the source object's material instead of the owner's. */
  useSourceMaterial: boolean;
  /** When true, the owner's base geometry is rendered alongside the instances. */
  showBase: boolean;
  /** Optional vertex group (by stable id) on the owner mesh that restricts
   *  placement to its member vertices. Null = use the whole mesh. */
  vertexGroupId: string | null;
  /** Runtime-resolved: source-space vertex indices belonging to `vertexGroupId`,
   *  populated by `resolveModifierEvaluation`. Never authored or persisted. */
  vertexGroupIndices?: Uint32Array | null;
}

export interface BlendShrinkwrapModifier
  extends BlendModifierBase<"shrinkwrap"> {
  /** Name of the target SceneObject to wrap to (null = no-op). */
  targetObjectName: string | null;
  /** How each source vertex finds its target position. */
  wrapMode: "nearest_surface" | "nearest_vertex" | "project";
  /** Distance to maintain from the target surface along the hit normal. */
  offset: number;
  /** Projection axis for "project" mode. */
  projectAxis: "x" | "y" | "z" | "normal";
  /** Allow projection in the negative axis direction ("project" mode only). */
  projectNegative: boolean;
  /** Allow projection in the positive axis direction ("project" mode only). */
  projectPositive: boolean;
  /** Optional vertex group (by stable id) restricting which vertices are moved. */
  vertexGroupId: string | null;
  /** Runtime-resolved flat index array for vertexGroupId. Never persisted. */
  vertexGroupIndices?: Uint32Array | null;
}

/** Source axis mapped onto the target curve's arc-length direction. */
export type CurveDeformAxis = "x" | "y" | "z" | "-x" | "-y" | "-z";

export interface BlendCurveModifier extends BlendModifierBase<"curve"> {
  /** Name of the target SceneObject whose curveData drives the deformation. */
  targetObjectName: string | null;
  /** Which owner-space axis is bent along the target curve. */
  deformAxis: CurveDeformAxis;
  /** When true, multiplies cross-section offsets by the target curve's radius. */
  useCurveRadius: boolean;
}

/** Procedural noise algorithm used by a single noise layer. */
export type NoiseType = "simplex" | "ridged" | "voronoi" | "worley";

/**
 * A single layer inside a Noise modifier.
 *
 * Layers are stacked additively: each layer samples its chosen noise field
 * at the vertex position (object-local), scales the result by `strength`,
 * and accumulates the displacement onto the per-axis enabled channels.
 *
 * Vertex group restriction is per-layer: if `vertexGroupId` is set, only
 * vertices inside that group receive this layer's contribution.
 */
export interface NoiseLayer {
  /** Stable id used as the ListControl selection key. */
  id: string;
  /** User-facing label shown in the layer list. */
  name: string;
  /** Per-layer enable toggle (the modifier itself also has one). */
  enabled: boolean;
  /** Which noise algorithm to sample. */
  noiseType: NoiseType;
  /** Displacement amplitude (world units at full strength). */
  strength: number;
  /** Feature size in object-local units — larger = coarser features. */
  scale: number;
  /** Integer seed; changing it re-randomizes the pattern without touching scale/strength. */
  seed: number;
  /** When true, this layer writes displacement to the X channel. */
  axisX: boolean;
  /** When true, this layer writes displacement to the Y channel. */
  axisY: boolean;
  /** When true, this layer writes displacement to the Z channel. */
  axisZ: boolean;
  /** Optional vertex group (by stable id) restricting which vertices this layer affects. */
  vertexGroupId: string | null;
  /** Runtime-resolved: source-space vertex indices belonging to `vertexGroupId`,
   *  populated by `resolveModifierEvaluation`. Never authored or persisted. */
  vertexGroupIndices?: Uint32Array | null;
}

let _nextNoiseLayerId = 1;

/** Generate a unique noise layer id. Matches the `createMaterialSlotId` pattern. */
export function createNoiseLayerId(): string {
  return `nlayer_${_nextNoiseLayerId++}_${Date.now().toString(36)}`;
}

/** Factory for a fresh noise layer with sensible defaults. */
export function createDefaultNoiseLayer(index = 1): NoiseLayer {
  return {
    id: createNoiseLayerId(),
    name: `Layer ${index}`,
    enabled: true,
    noiseType: "simplex",
    strength: 0.02,
    scale: 2,
    seed: 1,
    axisX: true,
    axisY: true,
    axisZ: true,
    vertexGroupId: null,
  };
}

export interface BlendNoiseModifier extends BlendModifierBase<"noise"> {
  /** Ordered list of noise layers. Accumulated additively in order. */
  layers: NoiseLayer[];
}

export interface BlendFollowObjectModifier
  extends BlendModifierBase<"followObject"> {
  /** Name of the target SceneObject to follow (null = no-op). Uses name, matching all other modifier targets. */
  targetObjectName: string | null;
  /** Bone name on a mannequin target to follow. null = follow the object root. */
  targetBoneName: string | null;
  /** Inherit the target's world-space position. The object's stored position is used as an additive offset. */
  followPosition: boolean;
  /** Inherit the target's world-space rotation. The object's stored rotation is post-multiplied on top. */
  followRotation: boolean;
  /** Inherit the target's world-space scale. The object's stored scale is multiplied on top. */
  followScale: boolean;
}

/** Which edge set a Bevel modifier should operate on. */
export type BevelLimitMethod = "none" | "angle" | "vertexGroup";

/** What geometry primitive the bevel is applied to. v1 supports edges only. */
export type BevelAffect = "edges" | "vertices";

/**
 * Bevel width interpretation — stored for Blender authoring fidelity.
 * v1 treats all modes as "offset" (the value read by the Rust kernel).
 */
export type BevelWidthType = "offset" | "width" | "depth" | "percent";

export interface BlendBevelModifier extends BlendModifierBase<"bevel"> {
  /** How the set of edges to bevel is chosen. */
  limitMethod: BevelLimitMethod;
  /** Dihedral threshold in degrees; used when `limitMethod === "angle"`. */
  angleLimit: number;
  /** Optional vertex group (by stable id) used when `limitMethod === "vertexGroup"`. */
  vertexGroupId: string | null;
  /** Runtime-resolved: source-space vertex indices belonging to `vertexGroupId`,
   *  populated by `resolveModifierEvaluation`. Never authored or persisted. */
  vertexGroupIndices?: Uint32Array | null;
  /** Geometry element to bevel. v1 supports "edges"; "vertices" is UI-disabled. */
  affect: BevelAffect;
  /** Width interpretation. Stored for fidelity; currently unused by the kernel. */
  widthType: BevelWidthType;
  /** Bevel width in object-local units. */
  offset: number;
  /** Number of profile segments (1–16). */
  segments: number;
  /** Profile shape (0 = concave, 0.5 = round, 1 = convex). */
  profile: number;
  /** Prevent overlapping bevels on narrow geometry. */
  clampOverlap: boolean;
  /** Mark newly created bevel edges as sharp. */
  markSharp: boolean;
  /** Mark newly created bevel edges as seam. */
  markSeam: boolean;
}

export interface BlendSmoothModifier extends BlendModifierBase<"smooth"> {
  /** Number of smoothing iterations (1–10). */
  iterations: number;
  /** Blend factor per iteration: 0 = no movement, 1 = full Laplacian step. */
  strength: number;
  /** Optional vertex group (by stable id) restricting which vertices are moved.
   *  Unmasked vertices are frozen but still count as neighbors in the average.
   *  Null = smooth the entire mesh. */
  vertexGroupId: string | null;
  /** Runtime-resolved flat index array for vertexGroupId. Never persisted. */
  vertexGroupIndices?: Uint32Array | null;
}

/** CSG operation applied between the owner mesh and the cutter mesh. */
export type BooleanOperation = "union" | "difference" | "intersect";

/**
 * CSG backend used to evaluate the Boolean op. Reserved enum so a future
 * "fast" BVH-based solver (e.g. three-bvh-csg) can be added without a
 * breaking change to authored scenes. For now only "exact" (manifold-3d)
 * is wired up.
 */
export type BooleanSolver = "exact";

export interface BlendBooleanModifier extends BlendModifierBase<"boolean"> {
  /** CSG operation performed with the cutter. */
  operation: BooleanOperation;
  /** Name of the SceneObject used as the cutter (null = pass-through). */
  targetObjectName: string | null;
  /** CSG backend. Reserved — only "exact" is supported today. */
  solver: BooleanSolver;
  /** When true, the solver is allowed to resolve self-intersections in the cutter. */
  selfIntersect: boolean;
  /**
   * Merge threshold (object-local units) for near-coincident vertices before
   * the op runs. 0 = disabled. Helps when cutter and owner share near-duplicate
   * vertices that would otherwise produce degenerate coplanar faces.
   */
  holeTolerance: number;
}

export type BlendModifier =
  | BlendSubsurfModifier
  | BlendMirrorModifier
  | BlendSolidifyModifier
  | BlendArrayModifier
  | BlendSkinModifier
  | BlendInstanceModifier
  | BlendShrinkwrapModifier
  | BlendCurveModifier
  | BlendNoiseModifier
  | BlendFollowObjectModifier
  | BlendBooleanModifier
  | BlendSmoothModifier
  | BlendBevelModifier;

/** Factory defaults for each modifier kind, used when adding modifiers at runtime. */
export const MODIFIER_DEFAULTS: Record<
  BlendModifier["kind"],
  () => BlendModifier
> = {
  subsurf: () => ({
    kind: "subsurf",
    name: "Subdivision",
    enabled: true,
    subdivType: 0,
    levels: 1,
    renderLevels: 1,
  }),
  solidify: () => ({
    kind: "solidify",
    name: "Solidify",
    enabled: true,
    offset: 0.02,
    offsetDirection: -1,
    vertexGroupId: null,
  }),
  mirror: () => ({
    kind: "mirror",
    name: "Mirror",
    enabled: true,
    axisX: true,
    axisY: false,
    axisZ: false,
    merge: true,
    clipping: true,
    tolerance: 0.001,
    mirrorObjectName: null,
    mirrorOrigin: null,
  }),
  array: () => ({
    kind: "array",
    name: "Array",
    enabled: true,
    count: 2,
    useRelativeOffset: true,
    relativeOffset: [1, 0, 0],
    useConstantOffset: false,
    constantOffset: [0, 0, 0],
    offsetObjectName: null,
    offsetMatrix: null,
  }),
  skin: () => ({
    kind: "skin",
    name: "Skin",
    enabled: true,
    depth: 0.05,
    subdivisions: 8,
    fillCaps: true,
    capStyle: "flat" as SkinCapStyle,
    preserveVolume: false,
    style: "smooth" as SkinStyle,
    strandCount: 3,
    twistRate: 3,
    bundleJitter: 0.3,
    ribFrequency: 8,
    ribDepth: 0.5,
    linkLength: 0.04,
    linkAspect: 0.6,
    wireThickness: 0.005,
    linkOverlap: 0.4,
    twistPerLink: 90,
    linkSegments: 24,
    vertexGroupId: null,
  }),
  instance: () => ({
    kind: "instance",
    name: "Instance",
    enabled: true,
    sourceObjectName: null,
    placementMode: "origin" as const,
    orientation: "local" as const,
    instanceLimit: 64 as const,
    scatterMode: false,
    instancesPerElement: 1,
    jitterEnabled: false,
    jitterScale: 0.25,
    jitterRotation: 1.0,
    useSourceMaterial: true,
    showBase: false,
    vertexGroupId: null,
  }),
  shrinkwrap: () => ({
    kind: "shrinkwrap" as const,
    name: "Shrink Wrap",
    enabled: true,
    targetObjectName: null,
    wrapMode: "nearest_surface" as const,
    offset: 0,
    projectAxis: "normal" as const,
    projectNegative: true,
    projectPositive: true,
    vertexGroupId: null,
  }),
  curve: () => ({
    kind: "curve" as const,
    name: "Curve",
    enabled: true,
    targetObjectName: null,
    deformAxis: "x" as const,
    useCurveRadius: false,
  }),
  followObject: () => ({
    kind: "followObject" as const,
    name: "Follow Object",
    enabled: true,
    targetObjectName: null,
    targetBoneName: null,
    followPosition: true,
    followRotation: true,
    followScale: false,
  }),
  noise: () => ({
    kind: "noise" as const,
    name: "Noise",
    enabled: true,
    layers: [createDefaultNoiseLayer(1)],
  }),
  boolean: () => ({
    kind: "boolean" as const,
    name: "Boolean",
    enabled: true,
    operation: "difference" as const,
    targetObjectName: null,
    solver: "exact" as const,
    selfIntersect: false,
    holeTolerance: 0,
  }),
  smooth: () => ({
    kind: "smooth" as const,
    name: "Smooth",
    enabled: true,
    iterations: 1,
    strength: 0.5,
    vertexGroupId: null,
  }),
  bevel: () => ({
    kind: "bevel" as const,
    name: "Bevel",
    enabled: true,
    limitMethod: "none" as const,
    angleLimit: 30,
    vertexGroupId: null,
    affect: "edges" as const,
    widthType: "offset" as const,
    offset: 0.02,
    segments: 1,
    profile: 0.5,
    clampOverlap: true,
    markSharp: true,
    markSeam: false,
  }),
};

/** Blender's ModifierMode flags */
export const MOD_MODE_REALTIME = 1 << 0;
